import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Button } from '~common/components/button'

// Configure PDF.js worker
const workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString()
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

export interface PdfViewerProps {
    pdfBlob: Blob | null
    className?: string
}

const MIN_SCALE = 0.5
const MAX_SCALE = 3.0
const SCALE_STEP = 0.25

export const PdfViewer: React.FC<PdfViewerProps> = ({ pdfBlob, className }) => {
    const [numPages, setNumPages] = useState<number | null>(null)
    const [pageNumber, setPageNumber] = useState(1)
    const [scale, setScale] = useState(1.0)
    const pdfContainerRef = useRef<HTMLDivElement>(null)

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages)
        setPageNumber(1)
    }

    useEffect(() => {
        const container = pdfContainerRef.current
        if (!container) return

        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            const link = target.closest('a')
            if (link && link.href) {
                event.preventDefault()
                window.open(link.href, '_blank', 'noopener,noreferrer')
            }
        }

        container.addEventListener('click', handleClick)
        return () => {
            container.removeEventListener('click', handleClick)
        }
    }, [])

    const goToPreviousPage = () => {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1)
        }
    }

    const goToNextPage = () => {
        if (numPages && pageNumber < numPages) {
            setPageNumber(pageNumber + 1)
        }
    }

    const zoomIn = () => {
        setScale((prev) => Math.min(prev + SCALE_STEP, MAX_SCALE))
    }

    const zoomOut = () => {
        setScale((prev) => Math.max(prev - SCALE_STEP, MIN_SCALE))
    }

    const downloadPdf = () => {
        if (pdfBlob) {
            const url = URL.createObjectURL(pdfBlob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'document.pdf'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        }
    }

    if (!pdfBlob) {
        return (
            <Container className={className}>
                <Placeholder>No PDF to display</Placeholder>
            </Container>
        )
    }

    return (
        <Container className={className}>
            <NavigationBar>
                <LeftControls>
                    {numPages && numPages > 1 && (
                        <Button
                            onClick={goToPreviousPage}
                            label="Previous"
                            icon="arrow-left"
                            iconPosition="left"
                            variant="ghost"
                            disabled={pageNumber <= 1}
                        />
                    )}
                </LeftControls>
                <CenterControls>
                    <PageIndicator>
                        {numPages
                            ? `Page ${pageNumber} of ${numPages}`
                            : 'Loading...'}
                    </PageIndicator>
                </CenterControls>
                <RightControls>
                    <ZoomControls>
                        <Button
                            onClick={zoomOut}
                            label="−"
                            variant="ghost"
                            disabled={scale <= MIN_SCALE}
                        />
                        <ZoomIndicator>
                            {Math.round(scale * 100)}%
                        </ZoomIndicator>
                        <Button
                            onClick={zoomIn}
                            label="+"
                            variant="ghost"
                            disabled={scale >= MAX_SCALE}
                        />
                    </ZoomControls>
                    <Button
                        onClick={downloadPdf}
                        label="Download"
                        icon="download"
                        variant="ghost"
                    />
                    {numPages && numPages > 1 && (
                        <Button
                            onClick={goToNextPage}
                            label="Next"
                            icon="arrow-right"
                            iconPosition="right"
                            variant="ghost"
                            disabled={pageNumber >= numPages}
                        />
                    )}
                </RightControls>
            </NavigationBar>
            <PdfContainer ref={pdfContainerRef}>
                <Document
                    file={pdfBlob}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                        <LoadingContainer>
                            <span>Loading PDF...</span>
                        </LoadingContainer>
                    }
                    error={
                        <ErrorContainer>
                            <ErrorText>Failed to load PDF</ErrorText>
                        </ErrorContainer>
                    }
                >
                    <Page
                        pageNumber={pageNumber}
                        scale={scale}
                        renderTextLayer
                        renderAnnotationLayer
                        className="pdf-page"
                    />
                </Document>
            </PdfContainer>
        </Container>
    )
}

const Container = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.background.bg1};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border-radius: ${({ theme }) => theme.borderRadius['2xl']};
`

const Placeholder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${({ theme }) => theme.colors.text.tertiary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
`

const NavigationBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${({ theme }) => theme.spacing[3]};
    background: ${({ theme }) => theme.colors.background.bg2};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    box-sizing: border-box;
    width: 100%;
    z-index: 1000;
    gap: ${({ theme }) => theme.spacing[2]};
`

const LeftControls = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    flex: 1;
`

const CenterControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
`

const RightControls = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    flex: 1;
    justify-content: flex-end;
`

const ZoomControls = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
`

const PageIndicator = styled.div`
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

const ZoomIndicator = styled.div`
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    min-width: 50px;
    text-align: center;
`

const PdfContainer = styled.div`
    flex: 1;
    overflow: auto;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: ${({ theme }) => theme.spacing[4]};

    .pdf-page {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        max-width: 100%;
        height: auto;
    }
`

const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
`

const ErrorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: ${({ theme }) => theme.spacing[4]};
`

const ErrorText = styled.div`
    color: ${({ theme }) => theme.colors.error};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
`
